import { useRef, useState } from "react"
import { useReactToPrint } from "react-to-print"
import ApprovalReceipt from "./ApprovalReceipt"
import { downloadEsmpFile } from "../utils/fileDownload"

export default function ESMPTable({
  data,
  emptyMessage,
  showReceiptButton = false,
  showReviewerComments = false,
  showDownloadButton = false,
  showAdminDecisionButton = false,
  onAdminDecision,
}) {
  const [selectedESMP, setSelectedESMP] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const receiptRef = useRef()

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: "ESMP Approval Receipt",
  })

  const handleShare = (esmp) => {
    const projectName = esmp.projectName || esmp.project_name || "N/A"
    const submittedBy = esmp.submittedBy || esmp.User?.name || "N/A"
    const shareText = `
ESMP APPROVAL RECEIPT

Project Name: ${projectName}
District: ${esmp.district}
Submitted By: ${submittedBy}
Status: ${esmp.status}
Approval Date: ${new Date().toLocaleDateString()}
    `
    if (navigator.share) {
      navigator.share({
        title: "ESMP Approval Receipt",
        text: shareText,
      })
    } else {
      navigator.clipboard.writeText(shareText)
      alert("Receipt details copied to clipboard")
    }
  }

  const handleDownload = async (esmp) => {
    try {
      setDownloadingId(esmp.id)
      await downloadEsmpFile(esmp, "admin")
    } catch (error) {
      console.error("Failed to download ESMP:", error)
      alert("The ESMP document could not be downloaded. Please try again.")
    } finally {
      setDownloadingId(null)
    }
  }

  // ✅ Return badge classes based on status
  const getStatusClasses = (status) => {
    switch (status) {
      case "Overdue":
        return "bg-red-100 text-red-700"
      case "Pending":
        return "bg-orange-100 text-orange-700"
      case "Returned":
        return "bg-yellow-100 text-yellow-700"
      case "Rejected":
        return "bg-red-100 text-red-700"
      case "Approved":
        return "bg-green-100 text-green-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  if (!data || data.length === 0) {
    return (
      <p className="text-gray-500 text-center py-6">
        {emptyMessage}
      </p>
    )
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border">Project Name</th>
              <th className="px-4 py-2 border">District</th>
              <th className="px-4 py-2 border">Submitted By</th>
              <th className="px-4 py-2 border">Status</th>
              {showReviewerComments && (
                <th className="px-4 py-2 border">Reviewer Comments</th>
              )}
              {showDownloadButton && (
                <th className="px-4 py-2 border">Document</th>
              )}
              {showAdminDecisionButton && (
                <th className="px-4 py-2 border">Admin Decision</th>
              )}
              {showReceiptButton && <th className="px-4 py-2 border">Receipt</th>}
            </tr>
          </thead>

          <tbody>
            {data.map((esmp) => (
              <tr key={esmp.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{esmp.projectName || esmp.project_name || "N/A"}</td>
                <td className="px-4 py-2 border">{esmp.district}</td>
                <td className="px-4 py-2 border">{esmp.submittedBy || esmp.User?.name || "N/A"}</td>

                <td className="px-4 py-2 border">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClasses(
                      esmp.status
                    )}`}
                  >
                    {esmp.status}
                  </span>
                </td>

                {showReviewerComments && (
                  <td className="px-4 py-2 border whitespace-pre-wrap">
                    {esmp.review_comments || "N/A"}
                  </td>
                )}

                {showDownloadButton && (
                  <td className="px-4 py-2 border">
                    {esmp.file_path || esmp.file_name ? (
                      <div className="flex min-w-[170px] flex-col items-start gap-2">
                        <span
                          className="max-w-[240px] truncate text-sm text-gray-600"
                          title={esmp.file_name || "ESMP document"}
                        >
                          {esmp.file_name || "ESMP document"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDownload(esmp)}
                          disabled={downloadingId === esmp.id}
                          className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          {downloadingId === esmp.id
                            ? "Downloading..."
                            : "Download"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No document available
                      </span>
                    )}
                  </td>
                )}

                {showAdminDecisionButton && (
                  <td className="px-4 py-2 border text-center">
                    {esmp.admin_decision === "Cleared for Submission" ? (
                      <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                        Final Cleared
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onAdminDecision?.(esmp)}
                        className="whitespace-nowrap rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700"
                      >
                        Make Decision
                      </button>
                    )}
                  </td>
                )}

                {showReceiptButton && (
                  <td className="px-4 py-2 border text-center">
                    <button
                      onClick={() => setSelectedESMP(esmp)}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-1.5 rounded-lg"
                    >
                      Generate Receipt
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showReceiptButton && selectedESMP && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[600px] rounded-xl shadow-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-bold">Approval Receipt</h3>
              <button
                onClick={() => setSelectedESMP(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <ApprovalReceipt ref={receiptRef} esmp={selectedESMP} />
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => handleShare(selectedESMP)}
                className="px-4 py-2 rounded-lg border border-green-600 text-green-700 hover:bg-green-50"
              >
                Share
              </button>

              <button
                onClick={() => setSelectedESMP(null)}
                className="px-4 py-2 rounded-lg border"
              >
                Close
              </button>

              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                Print / Save PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
